/**
 * The Wend mark, drawing itself: a path sets off from the dot, turns, and
 * arrives at the ring — the thing the product is named after, in motion.
 *
 * The geometry is the mark's own (`public/favicon.svg`, and `Mark` in
 * site-chrome.tsx): the same dot, path and ring under the same transform, so
 * the animated mark and the static one are one drawing. The motion is in
 * index.css (`wend-flow`, `wend-draw-fade`, `wend-draw-once`, `wend-ring-pulse`):
 *
 *   - `flow` (default) — the stroke draws in, holds, then leaves through the
 *     ring, forever; the ring breathes on the same beat.
 *   - `drawFade` — draws in, holds, fades out, and starts again.
 *   - `once` — draws in once on load and stays drawn (the header).
 *
 * `speed` sets the beat: slow 3.2s, normal 2.2s, fast 1.4s.
 *
 * Under `prefers-reduced-motion: reduce` every animation is switched off and
 * the base state — the fully drawn mark, ring at full strength — is what shows.
 * That is why the dash offset is 0 in the markup and only the keyframes move it.
 *
 * Decorative: the tile is aria-hidden. Whatever it sits in names it.
 */
import { useId, type CSSProperties } from 'react';

import { cn } from '@wend/ui';

export type WendMarkSpeed = 'slow' | 'normal' | 'fast';
export type WendMarkVariant = 'flow' | 'drawFade' | 'once';

const DURATION: Readonly<Record<WendMarkSpeed, string>> = {
  slow: '3.2s',
  normal: '2.2s',
  fast: '1.4s',
};

/** The mark's path, exactly. Exported so the site suite compares it with the favicon's. */
export const WEND_MARK_PATH = 'M40 150 L40 96 Q40 84 52 84 L108 84 Q120 84 120 72 L120 45';

export function WendMarkAnimated({
  size = 160,
  speed = 'normal',
  variant = 'flow',
  className,
}: {
  readonly size?: number;
  readonly speed?: WendMarkSpeed;
  readonly variant?: WendMarkVariant;
  readonly className?: string;
}) {
  // A gradient id per instance: the mark appears three times on the page.
  const gradientId = `wend-ring-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const style = {
    width: size,
    height: size,
    borderRadius: (size * 32) / 180,
    '--wend-dur': DURATION[speed],
  } as CSSProperties;

  return (
    <span
      aria-hidden="true"
      data-wend-mark={variant}
      className={cn('wend-mark inline-block flex-none overflow-hidden bg-deep', `wend-mark--${variant}`, className)}
      style={style}
    >
      <svg viewBox="0 0 220 220" width="100%" height="100%" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1="99" y1="51" x2="141" y2="9" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#6D5EF6" />
            <stop offset="1" stopColor="#9B5EF6" />
          </linearGradient>
        </defs>
        <g transform="translate(7.4,8.6) scale(1.2)">
          <circle cx="40" cy="150" r="10" fill="#FFFFFF" />
          <path
            className="wend-mark__path"
            d={WEND_MARK_PATH}
            pathLength={100}
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray="100 100"
            strokeDashoffset="0"
          />
          <circle className="wend-mark__ring" cx="120" cy="30" r="15" fill="none" stroke={`url(#${gradientId})`} strokeWidth="12" />
        </g>
      </svg>
    </span>
  );
}
